import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist/build/pdf"

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

// En iOS los PDF multipágina no se desplazan dentro de un iframe.
// Desktop: iframe. iOS + PDF: PDF.js en canvas con paginación.
export default class extends Controller {
  static targets = [
    "iframe",
    "iosViewer",
    "loading",
    "error",
    "viewer",
    "canvas",
    "pageLabel",
    "pagination",
    "prevButton",
    "nextButton",
    "firstButton",
    "lastButton"
  ]

  static values = {
    pdf: Boolean,
    url: String
  }

  connect() {
    this.currentPage = 1
    this.totalPages = 0
    this.pdfDocument = null
    this.renderTask = null

    if (!this.pdfValue || !this.isIos()) return

    this.activateIosViewer()
  }

  disconnect() {
    this.cancelRender()
    this.pdfDocument?.destroy()
    this.pdfDocument = null
  }

  async activateIosViewer() {
    this.iframeTarget.classList.add("d-none")
    this.iosViewerTarget.classList.remove("d-none")

    await this.loadPdf()
  }

  async loadPdf() {
    this.showLoading()

    try {
      const loadingTask = pdfjsLib.getDocument({
        url: this.urlValue,
        withCredentials: true
      })

      this.pdfDocument = await loadingTask.promise
      this.totalPages = this.pdfDocument.numPages
      this.currentPage = 1

      this.updatePagination()
      await this.renderPage(this.currentPage)
      this.showViewer()
    } catch (_error) {
      this.showError()
    }
  }

  async renderPage(pageNumber) {
    if (!this.pdfDocument) return

    this.cancelRender()

    const page = await this.pdfDocument.getPage(pageNumber)
    const baseViewport = page.getViewport({ scale: 1 })
    const containerWidth = this.canvasTarget.parentElement.clientWidth || baseViewport.width
    const displayScale = containerWidth / baseViewport.width
    const viewport = page.getViewport({ scale: displayScale })

    // En pantallas Retina (iPhone) el canvas debe renderizar a mayor resolución
    // que el tamaño CSS; si no, el navegador lo estira y se ve pixelado.
    const outputScale = Math.min(window.devicePixelRatio || 1, 3)

    const canvas = this.canvasTarget
    const context = canvas.getContext("2d")

    canvas.width = Math.floor(viewport.width * outputScale)
    canvas.height = Math.floor(viewport.height * outputScale)
    canvas.style.width = `${Math.floor(viewport.width)}px`
    canvas.style.height = `${Math.floor(viewport.height)}px`

    const transform =
      outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null

    this.renderTask = page.render({
      canvasContext: context,
      viewport,
      transform
    })

    await this.renderTask.promise

    this.currentPage = pageNumber
    this.updatePagination()
  }

  cancelRender() {
    if (this.renderTask) {
      this.renderTask.cancel()
      this.renderTask = null
    }
  }

  firstPage() {
    this.goToPage(1)
  }

  prevPage() {
    this.goToPage(this.currentPage - 1)
  }

  nextPage() {
    this.goToPage(this.currentPage + 1)
  }

  lastPage() {
    this.goToPage(this.totalPages)
  }

  async goToPage(pageNumber) {
    if (pageNumber < 1 || pageNumber > this.totalPages) return

    await this.renderPage(pageNumber)
  }

  updatePagination() {
    if (this.hasPageLabelTarget) {
      this.pageLabelTarget.textContent = `Página ${this.currentPage} de ${this.totalPages}`
    }

    const atStart = this.currentPage <= 1
    const atEnd = this.currentPage >= this.totalPages

    if (this.hasFirstButtonTarget) this.firstButtonTarget.disabled = atStart
    if (this.hasPrevButtonTarget) this.prevButtonTarget.disabled = atStart
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = atEnd
    if (this.hasLastButtonTarget) this.lastButtonTarget.disabled = atEnd

    if (this.hasPaginationTarget) {
      this.paginationTarget.classList.toggle("d-none", this.totalPages <= 1)
    }
  }

  showLoading() {
    this.loadingTarget.classList.remove("d-none")
    this.errorTarget.classList.add("d-none")
    this.viewerTarget.classList.add("d-none")
  }

  showError() {
    this.loadingTarget.classList.add("d-none")
    this.errorTarget.classList.remove("d-none")
    this.viewerTarget.classList.add("d-none")
  }

  showViewer() {
    this.loadingTarget.classList.add("d-none")
    this.errorTarget.classList.add("d-none")
    this.viewerTarget.classList.remove("d-none")
  }

  isIos() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    )
  }
}
